import React, { useRef, useState } from 'react';
import {
  CONFLICT_FOREIGN, CONFLICT_UNKNOWN, applyTransferFile, readTransferFile,
  storePassConflict, transferCodeIsValid,
} from './deviceTransfer';

/**
 * Д-3 — приём файла `.totalchat` на НОВОМ устройстве (дизайн §4).
 *
 * Живёт на лендинге приглашения: там человек оказывается с новым телефоном и
 * ссылкой в руках. Порядок жёсткий и держится не памятью, а кодом модуля
 * `deviceTransfer`: прочитать файл → спросить СЕРВЕР, жив ли доступ → и только
 * потом писать в браузер.
 *
 * 🔴 Почему проверка обязана быть первой. Участника мог удалить владелец, а
 * токен в файле живёт год. Запиши мы стор раньше проверки — человек получил бы
 * чат, который не открывается и не удаляется: ключи на месте, доступа нет, а
 * войти заново по ссылке мешает уже занятое хранилище. Поэтому при 401 в
 * браузер не ложится НИЧЕГО, и экран говорит прямо: просите новую ссылку.
 *
 * 🔴 Почему ошибки разделены. «Не тот файл», «не тот код» и «доступ закрыт»
 * лечатся по-разному: первое — искать другой файл, второе — бумажку с кодом,
 * третье — писать владельцу. Один общий текст «не удалось» отправил бы
 * человека делать не то.
 */

const STEP_IDLE = 'idle';
const STEP_WORKING = 'working';
const STEP_DENIED = 'denied';
/** Пояс-отказ: в браузере живёт чужой крипто-стор (решение владельца 04.09). */
const STEP_CLASH = 'clash';

/**
 * 🔴 Кого мы ждём в файле (ревью 03.09, High). На лендинге ЖДАТЬ некого:
 * панель показана только под `!alreadyIn`, то есть затирать в браузере нечего.
 * А на экране лечения участник ЕСТЬ — и `applyTransferFile` перезаписывает его
 * сессию, did и пароль стора данными ИЗ ФАЙЛА, ни с чем не сверяясь.
 *
 * Файл при этом сходится сам с собой: `readTransferFile` проверяет код, а
 * `POST /msg/members/me/token` — токен, лежащий в том же файле. Оба ответят
 * «всё в порядке» и для копии ПРЕЖНЕГО участия того же человека (дубли Д-2
 * реальны: у одного участника на бою четыре живые строки), и для файла соседа
 * по общему телефону. Итог был бы необратим: крипто-стор этого участника уже
 * снесён, сессия перезаписана, а did из файла либо ревокнут (403), либо ведёт
 * в чужую переписку.
 *
 * Поэтому вызывающий говорит, чей файл он согласен принять, и несовпадение
 * останавливает ВСЁ — до `onCheck` и до единой записи в браузер.
 */
function wrongOwner(expect, parcel) {
  if (!expect) return null;
  const fileMember = parcel?.session?.memberId ?? null;
  // 🔴 Решает `session.did`, а не `ls.msg_device_id` (ревью р.2, Medium).
  // Именно из него строится имя крипто-базы и DeviceId машины после
  // перезагрузки, тогда как `msg_device_id` — отдельный ключ, общий с
  // кабинетом владельца, и владелец его ротирует кнопкой Д-20. Сверяй мы его —
  // в таком браузере ЧЕСТНАЯ своя копия была бы отвергнута.
  const fileDid = parcel?.session?.did ?? null;
  if (fileMember === expect.memberId && fileDid === expect.did) return null;
  // 🔴 А `ls.msg_device_id` не сверяем ВОВСЕ — и это тоже вывод ревью (р.3).
  // Проверка «did сессии равен ключу хранилища» выглядела разумной и рубила бы
  // ЧЕСТНЫЕ файлы: в браузере, где рядом живёт кабинет, владелец ротирует этот
  // ключ кнопкой Д-20, а сессия участника остаётся прежней. Ловить там нечего:
  // ключи файла с его же участником сверяет `readTransferFile` (имя стора
  // строится из `session.did`), а `ls.msg_device_id` на восстановление не
  // влияет ничем.
  // 🔴 Участника сверяем ОТДЕЛЬНО от устройства, и это не перестраховка:
  // дубли Д-2 в одном браузере имеют ОДИН did (`join_did` = `browserDeviceId()`),
  // поэтому копию прежнего участия отличает ровно `memberId`. Прими её — и
  // машина представится `m41` под токеном `m42`: не расшифруется ничего, а
  // свой стор уже снесён.
  return 'Это копия другой переписки — файл снят не на этом устройстве.'
    + ' Нужен файл, сохранённый здесь же кнопкой «Копия и перенос».';
}


const DeviceImportPanel = ({
  onCheck, onDone, onClose, expect: expectOwner = null, mode = 'move',
}) => {
  // Одна панель на два места: перенос с лендинга и лечение внутри чата. Разные
  // там не действия, а ПРАВДА о положении дел — тексты, а не поток.
  const repair = mode === 'repair';
  const [step, setStep] = useState(STEP_IDLE);
  const [fileText, setFileText] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [clashReason, setClashReason] = useState(null);
  const inputRef = useRef(null);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setFileName(file.name);
    try {
      setFileText(await file.text());
    } catch {
      setFileText(null);
      setError('Файл не удалось прочитать.');
    }
  };

  const run = async () => {
    if (!fileText || !transferCodeIsValid(code)) return;
    setError(null);
    setStep(STEP_WORKING);
    try {
      // 1. Расшифровать. Ни одной записи в браузер здесь не происходит.
      const parcel = await readTransferFile(fileText, code);
      // 2. Чей это файл. Отказ ДО обращения к серверу и до любой записи.
      const foreign = wrongOwner(expectOwner, parcel);
      if (foreign) {
        setError(foreign);
        setStep(STEP_IDLE);
        return;
      }
      // 3. Пояс-отказ: не сломает ли перенос чужую переписку в этом браузере.
      //    Тоже ДО сервера и до записи — отказ здесь ничего не меняет вовсе.
      const clash = await storePassConflict(parcel);
      if (clash) {
        setClashReason(clash.reason);
        setStep(STEP_CLASH);
        return;
      }
      // 4. Спросить сервер. 401 — участника нет или файл старше года.
      const freshToken = await onCheck(parcel.session.token);
      // 5. Только теперь писать.
      await applyTransferFile(parcel, { freshToken });
      onDone?.(parcel);
    } catch (e) {
      if (e?.status === 401 || e?.status === 403) {
        setStep(STEP_DENIED);
        return;
      }
      // 🔴 Пояс из модуля тоже обязан приводить к ЧЕСТНОМУ экрану (ревью
      // 04.09, Medium). Он срабатывает в узкой гонке: между проверкой выше и
      // записью лежат сеть (`onCheck`) и `write`, и за это время соседняя
      // вкладка успевает поднять кабинет. Без этой ветки человек в самом
      // плохом случае получал бы одну голую строку без «что делать».
      if (e?.code === CONFLICT_FOREIGN || e?.code === CONFLICT_UNKNOWN) {
        setClashReason(e.code);
        setStep(STEP_CLASH);
        return;
      }
      setError(e?.message || 'Не удалось перенести переписку.');
      setStep(STEP_IDLE);
    }
  };

  if (step === STEP_CLASH) {
    return (
      <section
        className="msgTransferPanel"
        data-testid="import-clash"
        role="alert"
        // 🔴 Фокус и Escape (ревью 04.09, Low). Кнопка «Перенести переписку»
        // размонтирована, и без этого фокус падал бы на `body`: клавиатурой до
        // «Понятно» надо было добираться табом с начала страницы.
        tabIndex={-1}
        ref={(node) => node?.focus()}
        onKeyDown={(e) => { if (e.key === 'Escape') onClose?.(); }}
      >
        <h3>Здесь уже открыта другая переписка Total</h3>
        {/* 🔴 Говорим ПРИЧИНУ, а не «не удалось». Пароль хранилища в браузере
            один на всех, и перенос заменил бы его: соседняя переписка — чаще
            всего кабинет — осталась бы зашифрована фразой, которой больше нет
            нигде. Молчаливая замена и была тем дефектом, ради которого этот
            экран написан. */}
        <p className="msgTransferHint">
          {clashReason === CONFLICT_UNKNOWN
            ? 'Этот браузер не даёт проверить, какие переписки в нём уже открыты, '
              + 'а перенос заменяет общий для браузера пароль хранилища. '
            : 'В этом браузере живёт ещё одна переписка Total — например, кабинет. '
              + 'Пароль хранилища здесь один на всех, и перенос заменил бы его: '
              + 'соседняя переписка перестала бы открываться, и вернуть её было бы нечем. '}
          {/* 🔴 В лечении «в браузер ничего не записано» — НЕПРАВДА: мёртвый
              крипто-стор снесён ещё до панели (`startRepair`). Ровно на этой
              разнице ревью 03.09 поймало соседний экран «доступ закрыт». */}
          {/* 🔴 «Сообщения вернутся» было НЕПРАВДОЙ (ревью 04.09, Medium):
              лечение на этом устройстве доливает копию к живой истории
              (`writeDatabases` кладёт `put`), а в другом браузере истории нет
              вовсе — вернётся ровно то, что было в копии. Копия может быть
              старше стора на дни, и человек выбирает как раз в этот момент. */}
          {repair
            ? 'Поэтому лечение здесь остановлено. Ключи этого устройства уже стёрты, '
              + 'но файл цел: откройте ссылку-приглашение в другом браузере и '
              + 'восстановите переписку там — вернутся сообщения, которые были в '
              + 'копии, а пришедшие после неё на этом устройстве восстановить уже '
              + 'не выйдет. Если другого браузера нет, попросите новую ссылку: '
              + 'прежние сообщения не вернутся, но чат будет.'
            : 'Поэтому перенос остановлен — в браузер ничего не записано. '
              + 'Откройте ссылку в другом браузере или на отдельном устройстве '
              + 'и перенесите переписку там.'}
        </p>
        <button
          type="button"
          className="msgTransferClose"
          data-testid="import-clash-close"
          onClick={onClose}
        >
          Понятно
        </button>
      </section>
    );
  }

  if (step === STEP_DENIED) {
    return (
      <section
        className="msgTransferPanel"
        data-testid="import-denied"
        role="alert"
      >
        <h3>Доступ к этой переписке закрыт</h3>
        {/* 🔴 Текст зависит от места (ревью 03.09). На лендинге правда, что «в
            браузер ничего не записано». На экране лечения — НЕТ: мёртвый
            крипто-стор к этому моменту уже снесён, и обещать нетронутый
            браузер значило бы врать ровно там, где человек решает, что делать
            дальше. */}
        <p className="msgTransferHint">
          {repair
            ? 'Он мог быть закрыт владельцем или просрочен — файлу больше года. '
              + 'Ключи этого устройства уже стёрты, вернуть их этим файлом не '
              + 'вышло. Попросите новую ссылку-приглашение и войдите по ней: '
              + 'прежние сообщения не вернутся, но чат будет.'
            : 'Он мог быть закрыт владельцем или просрочен — файлу больше года. '
              + 'В браузер ничего не записано. Попросите новую ссылку-приглашение '
              + 'и войдите по ней: прежние сообщения не вернутся, но чат будет.'}
        </p>
        <button
          type="button"
          className="msgTransferClose"
          data-testid="import-denied-close"
          onClick={onClose}
        >
          Понятно
        </button>
      </section>
    );
  }

  return (
    <section
      className="msgTransferPanel"
      data-testid="import-panel"
      role="dialog"
      aria-labelledby="msgImportPanelTitle"
      onKeyDown={(e) => { if (e.key === 'Escape' && step !== STEP_WORKING) onClose?.(); }}
    >
      <div className="msgTransferPanelHead">
        <h3 id="msgImportPanelTitle">
          {repair ? 'Восстановить чат из копии' : 'Перенести чат с другого устройства'}
        </h3>
        <button
          type="button"
          data-testid="import-close"
          onClick={onClose}
          disabled={step === STEP_WORKING}
        >
          Закрыть
        </button>
      </div>

      <p className="msgTransferWhat">
        {repair
          ? 'Понадобится файл копии, сохранённый на этом же устройстве, и код, '
            + 'который оно показало, когда копию снимали.'
          : 'Понадобится файл, сохранённый на старом устройстве, и код переноса, '
            + 'который оно показало.'}
      </p>

      {error && (
        <p className="msgTransferError" role="alert" data-testid="import-error">{error}</p>
      )}

      <h4>Файл</h4>
      <input
        ref={inputRef}
        id="import-file"
        data-testid="import-file"
        type="file"
        accept=".totalchat,application/octet-stream"
        disabled={step === STEP_WORKING}
        onChange={pick}
      />
      {fileName && (
        <p className="msgTransferHint" data-testid="import-file-name">{fileName}</p>
      )}

      <h4>Код переноса</h4>
      <label className="msgTransferHint" htmlFor="import-code">
        {repair
          ? 'Двенадцать знаков тремя группами — те, что показала копия.'
          : 'Двенадцать знаков тремя группами, как показало старое устройство.'}
      </label>
      <input
        id="import-code"
        className="msgTransferCodeInput"
        data-testid="import-code"
        type="text"
        inputMode="latin"
        autoComplete="off"
        spellCheck="false"
        value={code}
        maxLength={20}
        placeholder="k7m4-p2x9-r5w8"
        disabled={step === STEP_WORKING}
        onChange={(e) => setCode(e.target.value)}
      />

      <button
        type="button"
        className="msgTransferGo"
        data-testid="import-go"
        disabled={step === STEP_WORKING || !fileText || !transferCodeIsValid(code)}
        onClick={run}
      >
        {step === STEP_WORKING && (repair ? 'Восстанавливаю…' : 'Переношу…')}
        {step !== STEP_WORKING && (repair ? 'Восстановить переписку' : 'Перенести переписку')}
      </button>
      {step !== STEP_WORKING && (!fileText || !transferCodeIsValid(code)) && (
        <p className="msgTransferHint" data-testid="import-go-locked">
          Выберите файл и введите код целиком.
        </p>
      )}
    </section>
  );
};

export default DeviceImportPanel;
